<script setup lang="ts">
import { onBeforeUnmount, useTemplateRef } from 'vue';
import { EKeyboardKey } from '@/utils/menu.utils';
import { registerBlockingDialogOpen } from './dialogQueue';

defineOptions({
  inheritAttrs: false,
});

const dialogRef = useTemplateRef<HTMLDialogElement>('dialogRef');
let releasePresence: (() => void) | null = null;

// Shared primitive for blocking app modals. It wraps <dialog>, keeps the
// dialog queue informed, and centralizes cancel/backdrop close behavior.
const props = withDefaults(
  defineProps<{
    canClose?: () => boolean;
    closeOnBackdropClick?: boolean;
    closeOnCancel?: boolean;
  }>(),
  {
    canClose: undefined,
    closeOnBackdropClick: true,
    closeOnCancel: true,
  },
);

const emit = defineEmits<{
  'backdrop-click': [event: MouseEvent];
  cancel: [event: Event];
  close: [];
}>();

const markDialogOpen = (): void => {
  if (releasePresence) return;
  releasePresence = registerBlockingDialogOpen(dialogRef.value ?? undefined);
};

const markDialogClosed = (): void => {
  releasePresence?.();
  releasePresence = null;
};

const showModal = (): void => {
  dialogRef.value?.showModal();
  markDialogOpen();
};

const close = (): void => {
  dialogRef.value?.close();
  markDialogClosed();
};

const canCloseDialog = (): boolean => props.canClose?.() ?? true;

const requestClose = (): void => {
  if (!canCloseDialog()) return;
  close();
};

const requestCancel = (event: Event): void => {
  event.preventDefault();
  emit('cancel', event);

  if (!props.closeOnCancel) return;
  requestClose();
};

const handleCancel = (event: Event): void => {
  // Ignore events such as the close event from a native fileexplorer, which would also close the native dialog.
  if (event.target !== dialogRef.value) {
    event.stopPropagation();
    return;
  }

  requestCancel(event);
};

const handleKeydown = (event: KeyboardEvent): void => {
  // Bubble Escape so nested controls can consume it before the dialog does.
  if (event.key !== EKeyboardKey.Escape) return;

  event.preventDefault();
  event.stopPropagation();
  requestCancel(event);
};

const handleBackdropClick = (event: MouseEvent): void => {
  const dialogElement = dialogRef.value;
  if (event.target !== dialogElement || event.currentTarget !== dialogElement) return;

  emit('backdrop-click', event);
  event.stopPropagation();
  event.preventDefault();

  if (!props.closeOnBackdropClick || !canCloseDialog()) return;

  close();
};

const handleClose = (): void => {
  markDialogClosed();
  emit('close');
};

defineExpose({
  close,
  get element() {
    return dialogRef.value;
  },
  showModal,
});

onBeforeUnmount(markDialogClosed);
</script>

<template>
  <dialog
    ref="dialogRef"
    v-bind="$attrs"
    closedby="none"
    @cancel="handleCancel"
    @close="handleClose"
    @keydown="handleKeydown"
    @mousedown="handleBackdropClick"
  >
    <slot></slot>
  </dialog>
</template>
